// LandingResolver.tsx — where "/" lands (Task 7, dashboard-shell cycle, handoff README §8).
// Free and Starter allow one bot, so sending every visit through the All-bots list first meant
// clicking into the only thing in it every session. "/" now resolves straight to the
// last-used bot's Overview; "/bots" keeps the list around for the Growth (multi-bot) case.
import { Link, Navigate } from 'react-router';
import { Skeleton } from '@repo/ui/components/skeleton';
import { useBots } from '../hooks/useBots';
import { LAST_BOT_KEY, resolveLandingPath } from '../lib/landing';
import { useT } from '../locales/context';

/** One shape per real element, at the real size (handoff README §6), standing in for the
 * Overview screen this resolver is about to redirect into — so the content area doesn't just
 * go blank while `useBots` is in flight. Task 8 owns Overview's real skeleton; this one only
 * has to survive the brief window before the redirect fires. */
function LandingSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-hidden="true">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_1fr]">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    </div>
  );
}

export function LandingResolver() {
  const t = useT();
  const { data: bots, isPending, error } = useBots();
  // A skeleton, never a redirect, while the list is unknown: redirecting on `undefined` would
  // flash the signup wizard at every existing customer for a frame — the trap this task exists
  // to avoid (handoff §8 / dispatch).
  if (isPending) return <LandingSkeleton />;
  // Fix round 1: `isPending` alone doesn't cover a persistent fetch failure — a settled query
  // with `data` still `undefined` used to fall through to the SAME skeleton forever (it's
  // `aria-hidden`, so a screen reader said nothing either), trapping the visitor at "/", the
  // one URL people bookmark, with no way out. Every other page in this app surfaces
  // `error.message` on a query failure; this is that same pattern, plus a way forward since
  // there's no retry button convention elsewhere to copy.
  if (error || !bots) {
    return (
      <div className="flex flex-col items-center gap-3 py-24 text-center" role="alert">
        <p className="text-sm text-destructive">{error?.message ?? t.auth.landingBotsErrorFallback}</p>
        <Link to="/bots" className="text-sm font-medium text-primary hover:underline">
          {t.auth.landingGoToBotsCta}
        </Link>
      </div>
    );
  }
  return <Navigate to={resolveLandingPath(bots, localStorage.getItem(LAST_BOT_KEY))} replace />;
}
